// @ts-strict-ignore
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { Observable } from 'rxjs';
import { GenerationResponseVO } from '../../../manager/generation-response.vo';
import { ProjectFinalizationInfoVO, ProjectHeadVO, ProjectVO } from '../../../model/vo/project.vo';
import { RepresentationDataVO, RepresentationVO } from '@shared/vo/representation.vo';
import { ProjectModel } from '../../../model/project.model';
import { ProjectUtils } from '../../project/util/project-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ReportVO } from '../../../model/vo/report.vo';
import { CameraParametersVO } from '@3d-scene/camera-parameters.vo';
import { PropertyVO, ReportGenerationPropertyVO } from '../../article/model/vo/property.vo';
import { UserProfileVO } from '@user/model/vo/user.vo';
import { ProjectFinalizationPreviewDTO } from '../../../model/vo/project-finalization-preview.dto';
import { DateFormatter } from '@shared/date-formatter';
import { ProjectStatesDef } from '../../project/constants/project-states-def';
import { Injectable } from '@angular/core';
import { ProjectDataService } from '../../../service/project-data.service';
import { StringUtils } from '@shared/util/string-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { ArticleNumberLong } from '../../../model/article-number-long';
import { IsProjectType } from '@project/model/is-project-type';
import {ProjectChangedType} from "@project/model/project-changed";

@Injectable({ providedIn: 'root' })
export class ProjectService {

  constructor(
    private projectModel: ProjectModel,
    private alertHelper: AlertHelper,
    private projectDataService: ProjectDataService,
    private localeResourceManager: LocaleResourceManager,
    private articleUsageService: ArticleUsageService
  ) {}

  public updateProjectData(projectId: number, projectVersion: number): void {
    this.projectDataService.loadProjectData(projectId, projectVersion).subscribe(
      (result: ProjectVO) => {
        if (
          this.projectModel.currentProject &&
          this.projectModel.currentProject.id === result.id &&
          this.projectModel.currentProject.currentVersion === result.currentVersion
        ) {
          this.projectModel.currentProject.finalizationState = result.finalizationState;
          this.projectModel.currentProject.articleNumber = result.articleNumber;
          this.projectModel.currentProject.articleNumberLong = result.articleNumberLong;
          this.projectModel.currentProject.drawingNumber = result.drawingNumber;
          this.projectModel.currentProject.typeClassId = result.typeClassId;
          this.projectModel.currentProject.fNumber = result.fNumber;
          this.projectModel.currentProject.isLatestVersion = result.isLatestVersion;
          this.projectModel.currentProject.documentVersion = result.documentVersion;
          this.projectModel.currentProject.originProjectArticleNumberLong = result.originProjectArticleNumberLong;
          this.projectModel.currentProject.isArticleNumberGenerated = result.isArticleNumberGenerated;
          this.projectModel.currentProject.isLocked = result.isLocked;
          this.projectModel.currentProject.totalDemand = result.totalDemand;
          this.projectModel.currentProject.batchSize = result.batchSize;
          this.projectModel.sendProjectChanged(ProjectChangedType.CurrentProjectChanged);
        }
      },
      (error: any) => {}
    );
  }

  public updateProjectDataLoaded(project: ProjectVO, representationData: RepresentationDataVO = null): void {
    if (project) {
      this.projectModel.updateProjectData(project);
      const view3DRepresentation: RepresentationVO = ProjectUtils.findFirstView3DRepresentation(this.projectModel.currentProject);
      if (view3DRepresentation && !view3DRepresentation.data) {
        view3DRepresentation.data = representationData;
      }
    }
  }

  public async deleteConfiguration(projectHeadVO: ProjectHeadVO): Promise<boolean> {
    const answer = await this.alertHelper.showAlert('deleteConfiguration.title.confirm_delete_configuration',
      'deleteConfiguration.message.confirm_delete_configuration', null, [AlertButton.YES, AlertButton.NO], "error");
    if (answer === AlertButton.YES) {
      try {
        await this.projectDataService.deleteConfiguration(projectHeadVO.id);
        this.projectModel.deleteConfiguration(projectHeadVO);
        return true;
      } catch (error) {
        this.alertHelper.showError('deleteConfiguration.title.delete_configuration',
          'deleteConfiguration.message.delete_configuration');
      }
    }
    return false;
  }

  public createFinalizationPreviewHandler(data: { projectId: number, project: ProjectVO, projectFinalizationInfo: ProjectFinalizationInfoVO,
    userProfile: UserProfileVO, reportProperties: ReportGenerationPropertyVO[], reports: ReportVO[], locale: string, cameraParameters: CameraParametersVO  }): Observable<GenerationResponseVO> {
    const dto: ProjectFinalizationPreviewDTO = new ProjectFinalizationPreviewDTO();
    dto.projectVO = data.project;
    dto.localeKey = data.locale;
    dto.projectFinalizationInfoVO = data.projectFinalizationInfo;
    dto.cameraParameters = data.cameraParameters;
    dto.projectProperty = data.reportProperties;
    dto.reportsVO = data.reports;
    dto.userProfile = data.userProfile;
    dto.timeZone = DateFormatter.userTimeZoneGMT();
    return this.projectDataService.createFinalizationPreview(data.projectId, dto);
  }

  public prepareProjectToSaveUnderNewName(): void {
    this.projectModel.currentProject.originProjectArticleNumberLong = this.projectModel.currentProject.articleNumberLong;
    this.projectModel.currentProject.articleNumber = '';
    this.projectModel.currentProject.articleNumberLong = new ArticleNumberLong();
    this.projectModel.currentProject.isArticleNumberGenerated = false;
    this.projectModel.currentProject.isLocked = false;
    this.projectModel.currentProject.drawingNumber = '';
    this.projectModel.currentProject.fNumber = '';
    this.projectModel.currentProject.finalizationState = ProjectStatesDef.FINALIZATION_STATE_NEW;
    this.projectModel.currentProject.typeClassId = '';
  }

  public showProjectLockedMessage(isError: boolean): Promise<AlertButton> {
    return this.alertHelper
      .showAlert(
        'load.title.project_is_locked_for_production',
        'load.message.project_is_locked_for_production',
        null,
        [AlertButton.OK],
        isError ? "exclamation" : "error"
      );
  }

  public getDefaultProjectName(): string {
    return this.localeResourceManager.getValueByKey('main.label.default_project_name');
  }

  public getProjectName(project: ProjectHeadVO): string {
    const projectName: string = this.getDefaultProjectName();
    if (!project || StringUtils.isEmpty(project.name)) {
      return projectName;
    }
    return project.name;
  }

  public isXcomSubprojectAvailable(project: ProjectVO): boolean {
    if (!IsProjectType.mounting(project)) {
      return false;
    }
    for (const articleUsage of ProjectUtils.articleUsages(project)) {
      const acceptingAreaIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getEngagedAcceptingConnectionUsageIds(articleUsage);
      if (ArrayUtils.isEmpty(acceptingAreaIds)) {
        continue;
      }
      const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleUsage);
      if (!article) {
        continue;
      }
      for (const metaElementIdentity of acceptingAreaIds) {
        const metaElement: ConnectionAreaVO = ArticleUtils.findMetaElementByName(article, metaElementIdentity.metaElementName) as ConnectionAreaVO;
        if (metaElement == null) {
          continue;
        }
        const typProperty: PropertyVO = PropertyUtils.getPropertyByName(metaElement.connectionRequirements, ArticleConnectionPropertyDef.TYPE);
        if (PropertyUtils.containsStringPropertyValue(typProperty, ArticleConnectionPropertyValue.XCOM_TYPE)) {
          return true;
        }
      }
    }
    return false;
  }

  public getCustomArticleNumbers(project: ProjectVO): string[] {
    return ProjectUtils.articleUsages(project)
      .filter(articleUsage => this.articleUsageService.isCustomArticleUsage(articleUsage))
      .map(CustomArticleUtils.getCustomArticleNumber);
  }

  public isLockingClipProject(project: ProjectVO): boolean {
    for (const articleUsage of ProjectUtils.getFirstArticleUsages(project)) {
      const article = this.articleUsageService.getArticleByArticleUsage(articleUsage);
      if (ArticleUtils.getArticleMountingType(article) === 'Rastfuss') {
        return true;
      }
    }
    return false;
  }

  public getArticles(project: ProjectVO): ArticleVO[]{
    return ProjectUtils.articleUsages(project)
      .map(articleUsage => this.articleUsageService.getArticleByArticleUsage(articleUsage));
  }

  public getFirstArticle(project: ProjectVO) {
    return this.articleUsageService.getArticleByArticleUsage(ProjectUtils.getFirstArticleUsage(project));
  }

  public getArticleWithNotDeliveredStatus(project: ProjectVO): ArticleVO[] {
    const notDeliveredArticles = new Map<string, ArticleVO>();

    for (const articleUsage of ProjectUtils.articleUsages(project)) {
        const article: ArticleVO = this.articleUsageService.getArticleOrCompositeArticleByArticleUsage(articleUsage)
        if (article != null &&  ArticleUtils.isStatusNotDelivered(article.materialStatus)) {
          notDeliveredArticles.set(article.articleNo, article);
        }
    }
    return Array.from(notDeliveredArticles.values());
  }

  public async checkConfirmCloseProject(
    title: string,
    message: string,
  ): Promise<boolean> {
    if (this.projectModel.currentProjectRequiresSaving) {
      const answer = await this.alertHelper
        .showAlert(
          title,
          message,
          {},
          [ AlertButton.YES, AlertButton.NO ],
          'error',
        );
      return answer === AlertButton.YES;
    } else {
      return true;
    }
  }
}
